import { ConsoleCategory, SqlLevel } from '../../types/console';
import type { DataEngineeringTopicId } from '../../types/console';
import type { PythonTopicId } from '../../types/console';

export interface RoadmapStepTarget {
  category: ConsoleCategory;
  level?: SqlLevel;
  topicId?: DataEngineeringTopicId;
  pythonTopicId?: PythonTopicId;
}

export interface RoadmapStep {
  id: string;
  order: number;
  title: string;
  description: string;
  target: RoadmapStepTarget;
}

/**
 * Roadmap de Ingeniería de Datos desde 0.
 * Cada paso lleva a una práctica concreta en /cursos.
 */
export const roadmapSteps: RoadmapStep[] = [
  {
    id: 'sql-inicial',
    order: 1,
    title: 'SQL desde cero',
    description: 'Consultas básicas, SELECT, filtros y ordenamiento.',
    target: { category: 'sql', level: 'inicial' },
  },
  {
    id: 'de-origen',
    order: 2,
    title: 'Cómo comienza la Ingeniería de Datos',
    description: 'El problema OLTP, la capa analítica y el nacimiento del rol.',
    target: { category: 'data-engineering', topicId: 0 },
  },
  {
    id: 'de-oltp-olap',
    order: 3,
    title: 'OLTP vs OLAP',
    description: 'Sistemas transaccionales vs analíticos.',
    target: { category: 'data-engineering', topicId: 1 },
  },
  {
    id: 'python-intro',
    order: 4,
    title: 'Python: introducción',
    description: 'Variables, tipos y primeros pasos en código.',
    target: { category: 'python', pythonTopicId: -2 },
  },
  {
    id: 'sql-avanzado',
    order: 5,
    title: 'SQL avanzado',
    description: 'JOINs, agregaciones y subconsultas.',
    target: { category: 'sql', level: 'avanzado' },
  },
  {
    id: 'de-etl',
    order: 6,
    title: 'ETL: Extraer, Transformar, Cargar',
    description: 'El ciclo clásico de datos.',
    target: { category: 'data-engineering', topicId: 2 },
  },
  {
    id: 'python-funciones',
    order: 7,
    title: 'Python: estructuras y funciones',
    description: 'Funciones, listas y bucles.',
    target: { category: 'python', pythonTopicId: 0 },
  },
  {
    id: 'de-warehouse',
    order: 8,
    title: 'Data Warehouse',
    description: 'Almacenes de datos para análisis.',
    target: { category: 'data-engineering', topicId: 3 },
  },
  {
    id: 'sql-examenes',
    order: 9,
    title: 'Exámenes técnicos SQL',
    description: 'Práctica tipo entrevista.',
    target: { category: 'sql', level: 'examenes' },
  },
  {
    id: 'python-datos',
    order: 10,
    title: 'Python: trabajando con datos',
    description: 'Lectura y manipulación de datos.',
    target: { category: 'python', pythonTopicId: 2 },
  },
];

const STORAGE_KEY = 'datadventure-roadmap-completed';

export function getCompletedStepIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function markStepCompleted(stepId: string): void {
  const completed = getCompletedStepIds();
  if (completed.includes(stepId)) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...completed, stepId]));
}

export function isStepUnlocked(stepId: string, completedIds: string[]): boolean {
  const step = roadmapSteps.find((s) => s.id === stepId);
  if (!step) return false;
  if (step.order === 1) return true;
  const prev = roadmapSteps.find((s) => s.order === step.order - 1);
  return prev ? completedIds.includes(prev.id) : true;
}
